import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCamera } from 'react-icons/fa';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import Modal from '../layouts/Modal';
import photoAlbumService from '../../services/photoAlbumService';
import Button from '../misc/Button';
import ToggleSwitch from '../misc/ToggleSwitch';
import { PhotoAlbum } from '../../types/photoAlbum';

interface EditPhotoAlbumModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  album: PhotoAlbum | null;
  onSave?: (updated: Partial<PhotoAlbum>) => void;
}

export default function EditPhotoAlbumModal({ open, setOpen, album, onSave }: EditPhotoAlbumModalProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [openToUpload, setOpenToUpload] = useState(true);
  const [coverPhoto, setCoverPhoto] = useState<File | undefined>(undefined);
  const [coverPreview, setCoverPreview] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!album) return;
    setTitle(album.title);
    setDescription(album.description || '');
    setOpenToUpload(Boolean(album.openToUpload));
    setCoverPhoto(undefined);
    if (coverPreview) {
      URL.revokeObjectURL(coverPreview);
      setCoverPreview(undefined);
    }
  }, [open, album]);

  useEffect(() => {
    return () => {
      if (coverPreview) URL.revokeObjectURL(coverPreview);
    };
  }, [coverPreview]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    const file = files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('component.modal.editAlbum.invalidImageType'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('component.modal.editAlbum.imageTooLarge'));
      return;
    }

    try {
      const { resizeImage } = await import('../../utils/formatUtils');
      const resized = await resizeImage(file, 768);
      setCoverPhoto(resized);
      if (coverPreview) URL.revokeObjectURL(coverPreview);
      setCoverPreview(URL.createObjectURL(resized));
    } catch (err) {
      console.error('Error processing image:', err);
      toast.error(t('component.modal.editAlbum.imageProcessingError'));
    }
  };

  const handleRemoveImage = () => {
    setCoverPhoto(undefined);
    if (coverPreview) {
      URL.revokeObjectURL(coverPreview);
      setCoverPreview(undefined);
    }
  };

  const handleSave = async () => {
    if (!album) return;

    if (!title.trim() || title.length < 3 || title.length > 50) {
      toast.error(t('component.modal.editAlbum.titleInvalid'));
      return;
    }

    setLoading(true);
    try {
      const updated = {
        title: title.trim(),
        description: description.trim(),
        openToUpload,
      } as Partial<PhotoAlbum>;

      await photoAlbumService.updatePhotoAlbum(album.communityId, album.id, updated);

      let { coverUrl } = album;
      if (coverPhoto) {
        const updatedWithCover = await photoAlbumService.updatePhotoAlbumCover(album.id, coverPhoto, album.communityId);
        coverUrl = updatedWithCover.coverUrl;
      }

      toast.success(t('page.photoAlbum.updated'));
      onSave?.({ ...updated, coverUrl });
      setOpen(false);
    } catch (err) {
      console.error('Error saving album:', err);
      toast.error(t('page.createPhotoAlbum.errors.createError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} title={t('component.modal.editAlbum.title')}>
      <div className="flex flex-col gap-6">
        <div>
          <label className="mb-1 block text-sm font-medium text-primary">
            {t('component.modal.editAlbum.coverImage')}
          </label>
          <div className="flex items-center gap-4">
            <div className="relative h-20 w-20 overflow-hidden rounded-lg bg-primary-300 bg-opacity-20">
              {coverPreview ? (
                <img src={coverPreview} alt="New cover preview" className="h-full w-full object-cover" />
              ) : album?.coverUrl ? (
                <img src={album.coverUrl} alt={album.title} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <FaCamera className="h-6 w-6 text-primary" />
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <label className="cursor-pointer rounded-lg bg-primary px-3 py-2 text-sm font-medium text-secondary">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                {t('component.modal.editAlbum.changeCover')}
              </label>
              {coverPreview && (
                <Button variant="danger" onClick={handleRemoveImage} className="text-sm">
                  {t('general.delete')}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="album-title" className="mb-1 block text-sm font-medium text-primary">
            {t('general.title')}
          </label>
          <input
            id="album-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={classNames('w-full rounded-lg border-none !bg-secondary-50 p-2')}
          />
        </div>

        <div>
          <label htmlFor="album-description" className="mb-1 block text-sm font-medium text-primary">
            {t('general.description')}
          </label>
          <textarea
            id="album-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full resize-none rounded-lg border-none p-2"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-medium text-primary">
            {t('component.modal.editAlbum.uploadSetting')}
          </label>
          <ToggleSwitch
            leftLabel={t('component.modal.editAlbum.uploadSettingRestricted')}
            rightLabel={t('component.modal.editAlbum.uploadSettingOpen')}
            value={openToUpload}
            onChange={(v) => setOpenToUpload(v)}
          />
        </div>

        <div className="mt-4 flex justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => setOpen(false)} className="px-6 py-2">
            {t('general.cancel')}
          </Button>
          <Button type="button" variant="primary" disabled={loading} onClick={handleSave} className="px-6 py-2">
            {loading ? t('general.saving') : t('general.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
